// src/studio/components/InteractionViewerModal.jsx
//
// Shows one Conversation Context item in the shared DynamicFileViewer. The
// backend decides the format (original PDF/TXT, a DOCX as PDF, or the text
// extracted at upload); this only maps its content type onto the viewer's
// filename-based dispatch.
//
// Usage:
//   <InteractionViewerModal projectId={id} interaction={row} onClose={...} />
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Spinner, Alert, EmptyState, ErrorBoundary } from "../../shared/ui";
import DynamicFileViewer from "../../components/FileViewers/DynamicFileViewer";
import { getApiError } from "../../api/client";
import { studioInteractionsApi } from "../api/studioApi";

function viewerFilename(contentType) {
  return contentType.includes("application/pdf") ? "content.pdf" : "content.txt";
}

export default function InteractionViewerModal({ projectId, interaction, onClose }) {
  // Keyed by the interaction it was loaded for, so a result left over from
  // the previously viewed item reads as "still loading" for the next one.
  const [loaded, setLoaded] = useState({ id: null, file: null, error: null });
  const interactionId = interaction?.id;

  useEffect(() => {
    if (interactionId == null) return undefined;
    let active = true;
    let objectUrl = null;

    studioInteractionsApi
      .getInteractionContent(projectId, interactionId)
      .then(({ blob, contentType }) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setLoaded({
          id: interactionId,
          file: { url: objectUrl, filename: viewerFilename(contentType), isEmpty: blob.size === 0 },
          error: null,
        });
      })
      .catch((err) => {
        console.error("Failed to load context item preview:", err);
        if (active) setLoaded({ id: interactionId, file: null, error: getApiError(err) });
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [projectId, interactionId]);

  const current = loaded.id === interactionId ? loaded : { file: null, error: null };

  let body;
  if (current.error) {
    body = <Alert variant="danger" title="Couldn't load the preview">{current.error}</Alert>;
  } else if (current.file?.isEmpty) {
    body = <EmptyState title="No content" description="This context item has no text content to show." />;
  } else if (current.file) {
    body = (
      <ErrorBoundary title="Preview unavailable">
        <DynamicFileViewer fileUrl={current.file.url} filename={current.file.filename} />
      </ErrorBoundary>
    );
  } else {
    body = <div className="flex justify-center py-16"><Spinner /></div>;
  }

  return (
    <Modal open={interactionId != null} onClose={onClose} title={interaction?.title || "Context"} size="lg">
      {body}
    </Modal>
  );
}

InteractionViewerModal.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  interaction: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};
